import React from 'react';
import {
    SortableContainer as sortableContainer,
    SortableElement as sortableElement,
    SortableHandle as sortableHandle
} from 'react-sortable-hoc';
import { Button, FormControl, FormHelperText, IconButton, ListItem, styled, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import DragHandle from '@mui/icons-material/DragHandle';
import MoreVert from '@mui/icons-material/MoreVert';
import { Field, FieldAttributes, FieldProps } from 'formik';
import { addOpacity, arrayMove, removeArrayItem, titleize, updateArrayItem } from 'lib/helpers';
import { Row } from 'ui/Flex';
import { MenuOn } from 'ui/MenuOn';
import { MuiModal } from 'ui/MuiModal';

const PREFIX = 'SortableFormField';

const classes = {
    dragHandle: `${PREFIX}-dragHandle`,
    root: `${PREFIX}-root`,
    head: `${PREFIX}-head`
};

const StyledListItem = styled(ListItem)(({ theme }) => ({
    backgroundColor: addOpacity(theme.palette.background.paper, 0.9),
    padding: theme.spacing(0.5),
    zIndex: 1300,
    '&:hover': {
        backgroundColor: addOpacity(theme.palette.background.paper, 0.9)
    },
    [`& .${classes.dragHandle}`]: {
        cursor: 'pointer',
        height: '1em',
        width: '1em',
        flexBasis: '5%',
        marginRight: '30px'
    }
}));

const StyledMuiModal = styled(MuiModal)(({ theme }) => ({
    minWidth: theme.spacing(48)
}));

interface SortableFieldDefinition {
    width: string;
    label: string;
    key: string;
}

interface ItemFieldProps<Model> {
    item: Model;
    index: number;
    onEditItem: (index: number) => void;
    onDeleteItem: (index: number) => void;
    fields: SortableFieldDefinition[];
    internalIndex?: number;
}

const CustomDragHandle = sortableHandle(({ className }: { className: string }) => (
    <DragHandle className={className} />
));

const SortableItem = sortableElement((props: ItemFieldProps<any>) => <ItemField {...props} />);
const SortableContainer = sortableContainer(({ children }: { children: React.ReactNode[] }) => (
    <ul>{children}</ul>
));

const ItemField: React.FC<ItemFieldProps<any>> = props => {
    const { fields, item, onEditItem, onDeleteItem, internalIndex } = props;
    const menuItems = [
        {
            label: 'Edit',
            onClick: () => {
                onEditItem(internalIndex);
            }
        },
        {
            label: 'Delete',
            onClick: () => {
                onDeleteItem(internalIndex);
            }
        }
    ];
    const renderOn = React.useCallback(
        (toggle: (event: React.MouseEvent<HTMLButtonElement>) => void) => (
            <IconButton onClick={toggle} size="large">
                <MoreVert />
            </IconButton>
        ),
        []
    );
    return (
        <StyledListItem>
            <Row flex={1} align="space-between" valign="center">
                <CustomDragHandle className={classes.dragHandle} />
                {fields.map(field => (
                    <Typography
                        key={`sortable-item-${internalIndex}-${field.key}`}
                        style={{ flexBasis: field.width }}
                    >
                        {titleize((item[field.key] ?? '').replaceAll('_', ' '), true)}
                    </Typography>
                ))}
                <div style={{ flexBasis: '10%' }}>
                    <MenuOn items={menuItems} renderOn={renderOn} />
                </div>
            </Row>
        </StyledListItem>
    );
};

export interface FormComponentProps<Model> {
    value?: Partial<Model>;
    onSubmit: (value: Partial<Model>) => void;
    close: () => void;
    formTitle: string;
}

interface SortableFormFieldProps<Model> {
    label?: React.ReactNode;
    description?: string;
    fields: SortableFieldDefinition[];
    FormComponent: React.FunctionComponent<FormComponentProps<Model>>;
}

export function SortableFormField<Model extends { index?: number }>(
    props: FieldAttributes<SortableFormFieldProps<Model>>
) {
    const { label, description, fields, FormComponent, ...fieldProps } = props;
    return (
        <Field {...fieldProps}>
            {(childProps: FieldProps<Model[]>) => (
                <SortableFormFieldComponent<Model>
                    label={label}
                    description={description}
                    fields={fields}
                    FormComponent={FormComponent}
                    {...childProps}
                />
            )}
        </Field>
    );
}

interface SortableFormFieldComponentProps<Model> extends FieldProps<Model[]>, SortableFormFieldProps<Model> {}

function SortableFormFieldComponent<Model extends { index?: number }>(
    props: SortableFormFieldComponentProps<Model>
) {
    const { field, meta, label, description, fields, FormComponent } = props;
    const value = React.useMemo(() => field.value || [], [field.value]);
    const isError = meta.touched && !!meta.error;

    const [openEdit, setEdit] = React.useState(false);
    const [openAdd, setAdd] = React.useState(false);
    const [currentIndex, setCurrentIndex] = React.useState(-1);
    const currentItem: Partial<Model> = React.useMemo(() => value[currentIndex], [currentIndex, value]);

    const handleChange = React.useCallback(
        (newValue: Partial<Model>[]) => {
            field.onChange({ target: { value: newValue, name: field.name } });
        },
        [field]
    );

    const handleSortEnd = React.useCallback(
        ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
            handleChange(arrayMove(value, oldIndex, newIndex));
        },
        [handleChange, value]
    );

    const handleDeleteItem = React.useCallback(
        (index: number) => {
            handleChange(removeArrayItem(value, index));
        },
        [handleChange, value]
    );

    const handleSubmit = React.useCallback(
        (item: Partial<Model>) => {
            const arr = updateArrayItem(value, currentIndex, item);
            if (arr[currentIndex]) {
                arr[currentIndex].index = currentIndex + 1;
            }
            handleChange(arr);
        },
        [value, currentIndex, handleChange]
    );

    const handleEditItem = React.useCallback((index: number) => {
        setCurrentIndex(index);
        setEdit(true);
    }, []);

    const handleAddItem = React.useCallback(() => {
        setCurrentIndex(value.length);
        setAdd(true);
    }, [value.length]);

    const handleClose = React.useCallback(() => {
        setEdit(false);
        setAdd(false);
        setCurrentIndex(-1);
    }, []);

    const renderValue = (item: Model, index: number) => (
        <SortableItem
            fields={fields}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            key={`primary-action-${index}`}
            item={item}
            index={index}
            internalIndex={index}
        />
    );

    return (
        <FormControl fullWidth error={isError}>
            {label && (
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    {label}
                </Typography>
            )}
            <SortableContainer useDragHandle lockAxis="y" onSortEnd={handleSortEnd}>
                <Row flex={1} style={{ padding: 8 }}>
                    <div style={{ flexBasis: '5%', marginRight: '30px' }} />
                    {fields &&
                        fields.map(fieldDef => (
                            <Typography
                                sx={{ fontSize: '0.75rem', flexBasis: fieldDef.width }}
                                key={`sortable-title-${fieldDef.key}`}
                            >
                                {fieldDef.label}
                            </Typography>
                        ))}
                    <div style={{ flexBasis: '10%' }} />
                </Row>
                {value && value.map(renderValue)}
                <Button variant="outlined" onClick={handleAddItem}>
                    <Add />
                </Button>
            </SortableContainer>
            <StyledMuiModal className={classes.root} open={openEdit} onClose={handleClose}>
                <FormComponent
                    formTitle="Edit item"
                    close={handleClose}
                    value={currentItem}
                    onSubmit={handleSubmit}
                />
            </StyledMuiModal>
            <StyledMuiModal className={classes.root} open={openAdd} onClose={handleClose}>
                <FormComponent
                    formTitle="Add item"
                    close={handleClose}
                    value={currentItem}
                    onSubmit={handleSubmit}
                />
            </StyledMuiModal>
            {(isError || description) && (
                <FormHelperText sx={{ ml: 0 }} error={isError}>
                    {isError ? meta.error : description}
                </FormHelperText>
            )}
        </FormControl>
    );
}
